import { TransactionSearchDrawer } from "@components/transactions/TransactionSearchDrawer/TransactionSearchDrawer";
import { useCallback, useContext, useMemo } from "react";
import { FinancialAccount } from "@common/types/financialAccount";
import { useLockDate } from "@api/lock.api";
import { ReconciliationSummaryResponse } from "@common/contracts/reconciliation.contract";
import { TransactionListItem } from "@common/schemas/transaction.schema";
import { ReconciliationContext } from "./Reconciliation.context";
import { ReconciliationTransactionPreview } from "./ReconciliationTransactionPreview";
import { buildMatchSearchView } from "./matchSearchView";
import {
    getFindTransactionCandidateTooltip,
    isFindTransactionCandidateDisabled,
} from "./pairMatching";
import styles from "./Reconciliation.module.scss";

interface Props {
    financialAccount: FinancialAccount;
    onMatch: (transaction: TransactionListItem) => Promise<void>;
    summary: ReconciliationSummaryResponse;
}

export const ReconciliationFindTransaction: React.FC<Props> = ({
    financialAccount,
    onMatch,
    summary,
}) => {
    const { transactionToMatch, setTransactionToMatch } = useContext(
        ReconciliationContext,
    );
    const lockDate = useLockDate(financialAccount.entityId);

    const labelGetter = useCallback(
        (transaction: TransactionListItem) => {
            if (
                transaction.id ===
                transactionToMatch?.currentlyMatchedFinancialTransactionId
            ) {
                return "Current match";
            }
            return "Match";
        },
        [transactionToMatch],
    );

    const isDisabled = useCallback(
        (transaction: TransactionListItem) =>
            isFindTransactionCandidateDisabled({
                transaction,
                transactionToMatch,
                financialAccountId: financialAccount.id,
                lockDate,
                summary,
            }),
        [transactionToMatch, financialAccount.id, lockDate, summary],
    );

    const tooltipGetter = useCallback(
        (transaction: TransactionListItem) =>
            getFindTransactionCandidateTooltip({
                transaction,
                transactionToMatch,
                financialAccountId: financialAccount.id,
                lockDate,
                summary,
            }),
        [transactionToMatch, financialAccount.id, lockDate, summary],
    );

    const onCTAClick = useCallback(
        (transaction: TransactionListItem) => {
            if (isDisabled(transaction)) {
                return;
            }

            onMatch(transaction);
        },
        [isDisabled, onMatch],
    );

    const view = useMemo(
        () =>
            buildMatchSearchView({
                financialAccount,
                amount: transactionToMatch?.statementTransactionToMatch.amount,
                openingDate: summary.statementsOpeningDate,
                closingDate: summary.statementsClosingDate,
                additionalDays: summary.additionalDays,
                labelGetter,
                isDisabled,
                tooltipGetter,
                onCTAClick,
            }),
        [
            transactionToMatch,
            labelGetter,
            isDisabled,
            tooltipGetter,
            financialAccount,
            onCTAClick,
            summary.statementsOpeningDate,
            summary.statementsClosingDate,
            summary.additionalDays,
        ],
    );

    return (
        <TransactionSearchDrawer
            open={!!transactionToMatch}
            onClose={() => setTransactionToMatch(undefined)}
            view={view}
            showEntityFilter={false}
            showActionsFilter={false}
            header={
                <span className="font-weight-medium">
                    Find a match for statement transaction
                </span>
            }
            addon={
                transactionToMatch && (
                    <ReconciliationTransactionPreview
                        label="Statement transaction:"
                        transaction={
                            transactionToMatch.statementTransactionToMatch
                        }
                    />
                )
            }
            contentClassName={styles.findTransactionContent}
        />
    );
};
